import _ from 'lodash'
import { subMonths, isBefore, isDate } from 'date-fns'

// _.get(obj, 'a.b.c.d', defaultValue)
function getPathValue(obj, path, defaultValue = undefined) {
  return _.get(obj, path, defaultValue)
}

function setPathValue(obj, path, value) {
  const newObj = { ...obj }
  _.set(newObj, path, value)
  return newObj
}

// variable is ${a.b.c} or #{d.e.f}
function abstractPath(variable) {
  const matched = variable.match(/\{(.+)\}/)
  return matched ? matched[1] : ''
}

// express looks like: '${a.b.c}', '#{a.b.c}', 'abc ${a.b.c}: #{c.d.e}'
// if express = 'aaa ${a.b.c}: #{d.e.f}'.match(/(?!#|\$)(?!\{)(.+?)\}/g)
// the reslult is ["{a.b.c}", "{d.e.f}"]
function abstractVariables(express) {
  if (typeof express !== 'string') {
    return []
  }
  try {
    const reg = new RegExp(`(\\$|\\#){.+?}`, 'g')
    const found = express.match(reg)
    return found || []
  } catch (e) /* istanbul ignore next */ {
    return []
  }
}

// prefix should be $ for fundItem , # for terms
// express = '${a.b.c} : #{d.e.f}'
// variables = ['${a.b.c}', '#{d.e.f}']
// pairs = {'$': fundItem, '#': terms}
function replaceVariables(express, variables, pairs) {
  if (typeof express !== 'string' || !express) {
    return express
  }
  if (!Array.isArray(variables) || variables.length === 0) {
    return express
  }
  if (!pairs || (!pairs.$ && !pairs['#'])) {
    return express
  }
  let newExp = express
  try {
    // val such as '${a.b.c}'
    variables.forEach((val) => {
      // path such as a.b.c
      const path = abstractPath(val)
      // key such as '$' or '#'
      Object.keys(pairs).forEach((key) => {
        if (val.startsWith(key)) {
          // TODO: should have a map to define how to map undefined and null to a special string, default is ''
          const realValue = getPathValue(pairs[key], path, '')
          newExp = newExp.replace(val, realValue || '')
        }
      })
    })
    return newExp
  } catch (e) /* istanbul ignore next */ {
    return newExp
  }
}

// express = '${a.b.c} : #{d.e.f}'
// pairs = {'$': fundItem, '#': terms}
function replaceExpress(express, pairs) {
  const variables = abstractVariables(express)
  return replaceVariables(express, variables, pairs)
}

function fromTypeToKey(rule, type) {
  try {
    const found = rule.group.categories.find((x) => x.type === type)
    if (found) {
      return found.key
    }
    return ''
  } catch (e) {
    return ''
  }
}

// targetObj[targetPath] = sourceObj[sourcePath]
function mapPathValue(sourceObj, sourcePath, targetObj, targetPath) {
  const sourceValue = getPathValue(sourceObj, sourcePath, '')
  return setPathValue(targetObj, targetPath, sourceValue)
}

function sortBySpecificArray(sourceArray, sortTarget, valuePath) {
  let allSortTarget = []
  const extraTarget = []

  if (Array.isArray(sortTarget)) {
    allSortTarget = JSON.parse(JSON.stringify(sortTarget))
  } else if (typeof sortTarget === 'object') {
    const { first = [], last = [] } = sortTarget
    const restSortTarget = sourceArray
      .filter((sourceItem) => {
        const sourceItemValue = valuePath ? getPathValue(sourceItem, valuePath, '') : sourceItem
        return !first.includes(sourceItemValue) && !last.includes(sourceItemValue)
      })
      .map((sourceItem) => (valuePath ? getPathValue(sourceItem, valuePath, '') : sourceItem))
      .sort((a, b) => a.localeCompare(b))
    allSortTarget = [...first, ...restSortTarget, ...last]
  }

  sourceArray.forEach((source) => {
    const sourceValue = valuePath ? getPathValue(source, valuePath, '') : source
    if (!allSortTarget.some((target) => target === sourceValue)) {
      extraTarget.push(sourceValue)
    }
  })
  const completeTarget = allSortTarget.concat(extraTarget)
  return sourceArray.sort((a, b) => {
    const aValue = valuePath ? getPathValue(a, valuePath, '') : a
    const bValue = valuePath ? getPathValue(b, valuePath, '') : b
    return completeTarget.indexOf(aValue) - completeTarget.indexOf(bValue)
  })
}

const getSpecificAttrValue = ({
  originData,
  attrName = 'languageTag',
  attrValue,
  targetAttr = 'name',
  dataPath = '',
  valuePlaceholder = '-'
}) => {
  try {
    if (originData) {
      const tempData = Array.isArray(originData) ? originData : dataPath ? _.get(originData, dataPath) : originData
      const targetAttrValue = Array.isArray(tempData) ? tempData.filter((item) => item[attrName] === attrValue)[0][targetAttr] : tempData
      return targetAttrValue || valuePlaceholder
    }
  } catch (error) {
    return valuePlaceholder
  }

  return valuePlaceholder
}

const checkIsYoungFund = (launchDate) => {
  const checkDate = isDate(new Date(launchDate))

  if (checkDate) {
    const todayTimestamp = new Date().getTime()
    const youngFundStartDate = new Date(subMonths(todayTimestamp, 6)).getTime()
    const fundDateTimestamp = new Date(launchDate).getTime()

    return isBefore(youngFundStartDate, fundDateTimestamp)
  }
  return false
}

const replaceSpecialChars = (originString, target = '-') => {
  return originString
    .replace(/[` ~!@#$%^&*()\-_–+<>?:"{},./;'[\]]/g, target)
    .split(target)
    .filter((item) => item !== '')
    .join(target)
}

const toFixedCustom = (number, decimal = 0) => {
  let copiedDecimal = decimal
  const stringifiedNumber = number.toString()
  if (stringifiedNumber) {
    const decimalIndex = stringifiedNumber.indexOf('.')
    if (decimalIndex < 0) {
      let fraction = ''
      for (let i = 0; i < decimal; i++) {
        fraction += '0'
      }

      return `${stringifiedNumber}.${fraction}`
    }

    const numDigits = stringifiedNumber.length - 1 - decimalIndex
    if (numDigits <= decimal) {
      let fraction = ''
      for (let i = 0; i < decimal - numDigits; i++) {
        fraction += '0'
      }

      return stringifiedNumber + fraction
    }
    const digits = stringifiedNumber.split('')
    let pos = decimalIndex + decimal
    const roundDigit = digits[pos + 1]
    if (roundDigit > 4) {
      pos = pos === decimalIndex ? pos - 1 : pos
      digits[pos] = Number(digits[pos] || 0) + 1
      while (digits[pos] === 10 && pos !== 0) {
        digits[pos] = 0
        pos -= 1
        pos = pos === decimalIndex ? pos - 1 : pos
        digits[pos] = Number(digits[pos] || 0) + 1
      }
    }

    if (copiedDecimal === 0) {
      copiedDecimal -= 1
    }

    return digits.slice(0, decimalIndex + decimal + 1).join('')
  }

  return stringifiedNumber
}

const divideBy100 = (value) => {
  if (value.toString().includes('.')) {
    const [, decimals] = value.toString().split('.')
    return parseFloat((parseFloat(value) / 100).toFixed(decimals.length + 2))
  }
  return parseFloat(value) / 100
}

export {
  getPathValue,
  setPathValue,
  abstractVariables,
  replaceVariables,
  fromTypeToKey,
  abstractPath,
  replaceExpress,
  mapPathValue,
  sortBySpecificArray,
  getSpecificAttrValue,
  checkIsYoungFund,
  replaceSpecialChars,
  toFixedCustom,
  divideBy100
}
